import { FC, useState } from 'react';
import { Close, Save } from '@mui/icons-material';
import { Grid, Box, Button, Typography, ButtonGroup } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useParams, useHistory } from 'react-router-dom';
import { postSalesContractTenDayFinalContract } from '../../../../../fetch';
import { formatMoney } from '../../../../../helpers';
import { SalesContractImages } from '../approved-details/sales-contract-images';
import { ITenDayCompleteStatusDetails } from '../../../../../models';

interface ITenDayCompleteDetails {
  statusText?: string;
  tenDayDetails: ITenDayCompleteStatusDetails;
  refetchContractDetails: () => void;
  isLoadingTenDayCompleteDetails: boolean;
  isSaving: boolean;
  setIsSaving: any;
}

export const TenDayCompleteDetails: FC<ITenDayCompleteDetails> = ({
  statusText,
  tenDayDetails,
  refetchContractDetails,
  isLoadingTenDayCompleteDetails,
  isSaving,
  setIsSaving
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { buildId }: { buildId: string } = useParams();
  const [imageFormData, setImageFormData] = useState<any>('');
  const { enqueueSnackbar } = useSnackbar();

  const isDirty = () => {
    return imageFormData;
  };

  const resetData = () => {
    setImageFormData('');
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const formData = new FormData();
      formData.append('FinalContract', imageFormData);

      await postSalesContractTenDayFinalContract(buildId, formData);
      await refetchContractDetails();
      resetData();
      setIsSaving(false);
      enqueueSnackbar('Success, build updated!', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error saving Final Contract, please try again.`, {
        variant: 'error'
      });
      setIsSaving(false);
      console.log(error);
    }
  };

  return (
    <>
      <Box mt={1}>
        {!isLoadingTenDayCompleteDetails && !isSaving && statusText && (
          <Grid container>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.readOnlyHeaders} variant="h6">
                Build Info
              </Typography>
              <Typography variant="subtitle1">
                <span className={classes.detailsLabel}>Status:</span> {statusText} <br />
              </Typography>
              <Typography variant="subtitle1">
                <span className={classes.detailsLabel}>Accepted Contract Price:</span>{' '}
                {formatMoney(tenDayDetails?.contractedAcceptedAmount)} <br />
              </Typography>
              <Typography variant="subtitle1">
                <span className={classes.detailsLabel}>Ten Day Selections Total:</span>{' '}
                {tenDayDetails?.tenDayUpgradeAmount} <br />
              </Typography>
              <Typography variant="subtitle1">
                <span className={classes.detailsLabel}>Total Price:</span>{' '}
                {tenDayDetails?.totalPriceFC} <br />
              </Typography>
              <Typography variant="subtitle1" className={classes.upgradeCap}>
                <span>Option Cap Overage:</span> {formatMoney(tenDayDetails?.upgradeCapOverage)}{' '}
                <br />
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} className={classes.prevOffers}>
              <Typography className={classes.readOnlyHeaders} variant="h6">
                Accepted Offer
              </Typography>
              <Typography variant="subtitle1">
                <span className={classes.detailsLabel}>Offer Date:</span>{' '}
                {tenDayDetails?.acceptedOfferDate} <br />
              </Typography>
              <Typography variant="subtitle1">
                <span className={classes.detailsLabel}>Purchaser Offer:</span>{' '}
                {tenDayDetails?.contractedAcceptedAmount} <br />
              </Typography>
              <Typography variant="subtitle1">
                <span className={classes.detailsLabel}>Financing Type:</span>{' '}
                {tenDayDetails?.financingType} <br />
              </Typography>
              <Typography variant="subtitle1">
                <span className={classes.detailsLabel}>Lender Status:</span>{' '}
                {tenDayDetails?.lenderStatus} <br />
              </Typography>
            </Grid>
            <Grid container>
              <Grid item xs={12} md={8} lg={8} className={classes.finalContract}>
                <Grid container alignItems="center">
                  <Grid item xs={12} md={8} lg={8}>
                    <SalesContractImages
                      imageTitle={'Final Contract'}
                      image={null}
                      imageData={value => setImageFormData(value)}
                      uploadText={'Upload Final Contract'}
                    ></SalesContractImages>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Box>
      {!isLoadingTenDayCompleteDetails && !isSaving && statusText && (
        <ButtonGroup className={classes.buttonGroup}>
          <Button
            disabled={!imageFormData}
            type="submit"
            startIcon={<Save />}
            variant="contained"
            color="primary"
            onClick={() => {
              const result = window.confirm(
                'Saving will progress this build to closing status, are you sure you want to continue?'
              );
              if (result) {
                handleSave();
              } else {
                return;
              }
            }}
          >
            Save
          </Button>
          <Button
            type="button"
            variant="contained"
            color="inherit"
            startIcon={<Close />}
            onClick={() => {
              if (!isDirty()) {
                history.push('/sales/contracting/contract-acceptance');
              } else {
                const result = window.confirm(
                  'You have unsaved changes, are you sure you want to exit?'
                );
                if (result) {
                  history.push('/sales/contracting/contract-acceptance');
                } else {
                  return;
                }
              }
            }}
          >
            Cancel
          </Button>
        </ButtonGroup>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    marginTop: theme.spacing(1)
  },
  contractInfo: {
    fontWeight: '500',
    paddingRight: 8
  },
  readOnlyHeaders: {
    paddingBottom: theme.spacing(0.25),
    fontWeight: '700'
  },
  notSelected: {
    color: '#88888',
    opacity: 0.6
  },
  buttonText: {
    color: 'white'
  },
  detailsLabel: {
    fontWeight: 'bold'
  },
  offerAccepted: {
    '@media (min-width: 600px)': {
      marginTop: '-10px',
      paddingLeft: '30px'
    }
  },
  prevOffers: {
    '@media (min-width: 600px)': {
      paddingLeft: '30px'
    }
  },
  counterOffer: {
    paddingBottom: '0px'
  },
  finalContract: {
    paddingBottom: '16px'
  },
  buttonGroup: {
    paddingTop: '16px'
  },
  upgradeCap: {
    color: 'red',
    marginTop: '12px'
  }
}));
